import React, { useState } from "react";
import { toast } from "sonner";
import {
  Copy,
  Share2,
  Users,
  Gift,
  ChevronRight,
} from "lucide-react";

const REF_CODE = "DYNTRA25";
const REF_URL = `https://dyntra.com/ref/${REF_CODE}`;

const STEPS = [
  {
    title: "You Invite",
    sub: "Share your referral code with your friends.",
  },
  {
    title: "They Sign Up",
    sub: "Your friends sign up and place their first order.",
  },
  {
    title: "You Earn Rewards",
    sub: "You both get exciting rewards on successful referral.",
  },
];

const CHANNELS = [
  { id: "whatsapp", label: "WhatsApp", color: "#25D366" },
  { id: "facebook", label: "Facebook", color: "#1877F2" },
  { id: "instagram", label: "Instagram", color: "#E4405F" },
  { id: "email", label: "Email", color: "#4B2AAD" },
  { id: "more", label: "More", color: "#6B7280" },
];

export default function ReferEarn() {
  const [copied, setCopied] = useState("");

  const copy = (v, label) => {
    try {
      navigator.clipboard?.writeText(v);
      setCopied(label);
      toast.success(`Copied ${label}`);
      setTimeout(() => setCopied(""), 2000);
    } catch {
      toast.success("Copied");
    }
  };

  const share = (channel) => {
    const text = encodeURIComponent(`Sign up on Dyntra with my code ${REF_CODE} and we both earn rewards! ${REF_URL}`);
    if (channel === "whatsapp") window.open(`https://wa.me/?text=${text}`, "_blank");
    else if (channel === "facebook")
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(REF_URL)}`, "_blank");
    else if (channel === "email")
      window.open(`mailto:?subject=Join%20Dyntra&body=${text}`, "_self");
    else toast.info("Sharing is not supported here — copy the code and paste it manually.");
  };

  return (
    <div data-testid="account-refer" className="space-y-6">
      <header>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
          Refer &amp; Earn
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Invite your friends to Dyntra and both of you get exciting rewards on
          every successful referral.
        </p>
      </header>

      <div className="rounded-2xl bg-gradient-to-br from-[#4B2AAD] to-[#6B4CD9] text-white p-6 flex items-center gap-4 justify-between overflow-hidden">
        <div className="max-w-md">
          <p className="font-display text-xl sm:text-2xl font-bold">
            Good health is better together!
          </p>
          <p className="text-xs opacity-90 mt-1">
            Refer your friends to Dyntra and both of you get amazing rewards.
          </p>
        </div>
        <Gift className="h-16 w-16 opacity-80 hidden md:block" />
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Your Referral Code
        </p>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[180px] rounded-xl border border-dashed border-[#4B2AAD]/40 bg-[#F3EFFC] px-4 py-3">
            <p className="font-display text-2xl font-extrabold text-[#4B2AAD] tracking-wide">
              {REF_CODE}
            </p>
          </div>
          <button
            onClick={() => copy(REF_CODE, "Code")}
            data-testid="refer-copy-code"
            className="dyntra-btn-primary"
          >
            <Copy className="h-4 w-4" /> Copy Code
          </button>
          <button
            onClick={() => share("more")}
            className="dyntra-btn-ghost"
          >
            <Share2 className="h-4 w-4" /> Share Link
          </button>
        </div>

        <p className="mt-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Share your referral link and earn rewards when your friends place their first order.
        </p>
        <div className="mt-2 flex items-center gap-2">
          <input
            readOnly
            value={REF_URL}
            className="flex-1 rounded-xl border border-gray-200 bg-[#F5F3FB] px-3 py-2 text-sm outline-none"
          />
          <button
            onClick={() => copy(REF_URL, "Link")}
            className="dyntra-btn-ghost"
          >
            <Copy className="h-4 w-4" /> Copy Link
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <h3 className="font-display text-base font-bold text-gray-900">
          Invite Your Friends
        </h3>
        <p className="text-xs text-gray-500 mt-1">Choose how you want to invite</p>
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-3">
          {CHANNELS.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => share(c.id)}
              data-testid={`refer-share-${c.id}`}
              className="rounded-2xl border border-gray-100 bg-white p-3 flex flex-col items-center gap-1 hover:border-[#4B2AAD] transition-colors"
            >
              <div
                className="h-9 w-9 rounded-full text-white flex items-center justify-center"
                style={{ background: c.color }}
              >
                <Share2 className="h-4 w-4" />
              </div>
              <span className="text-xs font-semibold text-gray-700">{c.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <h3 className="font-display text-base font-bold text-gray-900">
          How It Works
        </h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          {STEPS.map((s, i) => (
            <div
              key={s.title}
              className="rounded-2xl bg-[#F5F3FB] p-4 relative"
            >
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-white flex items-center justify-center">
                  <Users className="h-4 w-4 text-[#4B2AAD]" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase text-[#4B2AAD] tracking-wider">
                    Step {i + 1}
                  </p>
                  <p className="text-sm font-bold text-gray-900">{s.title}</p>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-2">{s.sub}</p>
              {i < STEPS.length - 1 && (
                <ChevronRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#4B2AAD]" />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <h3 className="font-display text-base font-bold text-gray-900">
          Your Earnings Summary
        </h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
            <p className="text-xs text-emerald-700 font-semibold">Successful Referrals</p>
            <p className="mt-1 text-2xl font-display font-extrabold text-emerald-700">24</p>
          </div>
          <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4">
            <p className="text-xs text-amber-700 font-semibold">Pending Rewards</p>
            <p className="mt-1 text-2xl font-display font-extrabold text-amber-700">8</p>
          </div>
          <div className="rounded-2xl bg-[#F3EFFC] border border-[#4B2AAD]/20 p-4">
            <p className="text-xs text-[#4B2AAD] font-semibold">Total Earned</p>
            <p className="mt-1 text-2xl font-display font-extrabold text-[#4B2AAD]">₹3,600</p>
          </div>
        </div>
        <button className="mt-4 text-xs font-semibold text-[#4B2AAD] hover:underline">
          View Referral History →
        </button>
      </div>
    </div>
  );
}
